/* ==========================================================================
   Client News — client-side search, faceted filtering, sorting and reveals
   --------------------------------------------------------------------------
   Design notes:

   * The cards are rendered server-side by Liquid. This script never builds
     markup for an announcement — it only shows, hides and reorders nodes
     that already exist. That keeps the page indexable, keeps it working
     without JS, and means zero layout shift on load.

   * Every announcement carries its facets as data-attributes, so filtering
     is a pure attribute comparison with no parallel data structure to keep
     in sync with the DOM.

   * Filters are faceted: values within one group are OR'd, groups are AND'd,
     and each group's counts are computed against every OTHER active filter
     (the standard behaviour that stops counts from lying).

   Public contract with the Liquid template:
     [data-cn-root]                      section root
     [data-cn-item]                      one announcement card
     [data-cn-filter="<group>"]          a filter pill; data-value="" means "All"
     [data-cn-search]                    the search input
     [data-cn-sort]                      the sort <select>
     [data-cn-count]                     live results count
     [data-cn-empty]                     empty state
     [data-cn-clear]                     clear-filters button
   ========================================================================== */

(function () {
  'use strict';

  /** Facet groups, in the order they appear in the toolbar. */
  var GROUPS = ['type', 'industry', 'stage', 'year'];

  /** How long to wait after a keystroke before re-filtering. */
  var SEARCH_DEBOUNCE_MS = 120;

  /** Per-card delay in the staggered cascade, and the ceiling on it. */
  var STAGGER_STEP_MS = 45;
  var STAGGER_MAX_MS = 400;

  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ------------------------------------------------------------------ utils */

  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments;
      var ctx = this;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(ctx, args);
      }, wait);
    };
  }

  function escapeRegExp(str) {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  function escapeHtml(str) {
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /* ------------------------------------------------------------------ class */

  function ClientNews(root) {
    this.root = root;
    this.items = Array.prototype.slice.call(root.querySelectorAll('[data-cn-item]'));
    this.pills = Array.prototype.slice.call(root.querySelectorAll('[data-cn-filter]'));
    this.searchInput = root.querySelector('[data-cn-search]');
    this.searchClear = root.querySelector('[data-cn-search-clear]');
    // Custom sort listbox (a native <select> cannot style its open list).
    this.sortValueEl = root.querySelector('[data-cn-sort-value]');
    this.sortOptions = Array.prototype.slice.call(root.querySelectorAll('[data-cn-sort-option]'));
    this.sortTrigger = root.querySelector('[data-cn-facet="sort"] [data-cn-facet-trigger]');
    this.countEl = root.querySelector('[data-cn-count]');
    this.emptyEl = root.querySelector('[data-cn-empty]');
    // Two Clear buttons exist: one in the toolbar (auto-hidden when nothing
    // is filtered) and one in the empty state (always visible).
    this.clearBtns = Array.prototype.slice.call(root.querySelectorAll('[data-cn-clear]'));
    this.clearToggle = root.querySelector('[data-cn-clear-toggle]');
    this.grid = root.querySelector('[data-cn-grid]');
    this.toolbar = root.querySelector('[data-cn-toolbar]');
    this.facets = Array.prototype.slice.call(root.querySelectorAll('[data-cn-facet]'));

    // Active facet selections: { type: Set, industry: Set, ... }
    this.active = {};
    GROUPS.forEach(
      function (g) {
        this.active[g] = new Set();
      }.bind(this)
    );

    this.query = '';
    this.sort = 'newest';

    this.cache();
    this.readStateFromUrl();
    this.bind();
    this.initFacets();
    this.initStuck();

    // Signals to CSS that the hidden-then-reveal start state is safe to apply.
    root.classList.add('cn-js');

    this.apply({ initial: true });
    this.observeReveals();
  }

  /**
   * Snapshot each card's facets and its original headline/summary text once,
   * so search highlighting can be re-rendered from a clean source every time.
   */
  ClientNews.prototype.cache = function () {
    this.items.forEach(function (item) {
      var headline = item.querySelector('[data-cn-headline]');
      var summary = item.querySelector('[data-cn-summary]');

      item._cn = {
        type: item.getAttribute('data-type') || '',
        industry: item.getAttribute('data-industry') || '',
        stage: item.getAttribute('data-stage') || '',
        year: item.getAttribute('data-year') || '',
        date: item.getAttribute('data-date') || '',
        company: (item.getAttribute('data-company') || '').toLowerCase(),
        amount: parseFloat(item.getAttribute('data-amount') || '0') || 0,
        search: (item.getAttribute('data-search') || '').toLowerCase(),
        headlineEl: headline,
        summaryEl: summary,
        headlineText: headline ? headline.textContent : '',
        summaryText: summary ? summary.textContent : ''
      };
    });
  };

  /* ------------------------------------------------------------- URL state */

  /** Hydrate filters from ?type=a,b&industry=c&q=…&sort=… so views are shareable. */
  ClientNews.prototype.readStateFromUrl = function () {
    var params = new URLSearchParams(window.location.search);

    GROUPS.forEach(
      function (g) {
        var raw = params.get(g);
        if (!raw) return;
        raw.split(',').forEach(
          function (v) {
            if (v) this.active[g].add(v);
          }.bind(this)
        );
      }.bind(this)
    );

    var q = params.get('q');
    if (q && this.searchInput) {
      this.query = q;
      this.searchInput.value = q;
    }

    var sort = params.get('sort');
    if (sort && this.sortOptions.some(function (o) {
      return o.getAttribute('data-cn-sort-option') === sort;
    })) {
      this.setSort(sort, { silent: true });
    }
  };

  ClientNews.prototype.writeStateToUrl = function () {
    var params = new URLSearchParams(window.location.search);

    GROUPS.forEach(
      function (g) {
        var vals = Array.from(this.active[g]);
        if (vals.length) params.set(g, vals.join(','));
        else params.delete(g);
      }.bind(this)
    );

    if (this.query) params.set('q', this.query);
    else params.delete('q');

    if (this.sort && this.sort !== 'newest') params.set('sort', this.sort);
    else params.delete('sort');

    var qs = params.toString();
    var url = window.location.pathname + (qs ? '?' + qs : '');
    window.history.replaceState(null, '', url);
  };

  /* ---------------------------------------------------------------- events */

  ClientNews.prototype.bind = function () {
    var self = this;

    this.pills.forEach(function (pill) {
      pill.addEventListener('click', function () {
        var group = pill.getAttribute('data-cn-filter');
        var value = pill.getAttribute('data-value') || '';

        if (!value) {
          // "All" clears the whole group.
          self.active[group].clear();
        } else if (self.active[group].has(value)) {
          self.active[group].delete(value);
        } else {
          self.active[group].add(value);
        }

        self.apply();
      });
    });

    if (this.searchInput) {
      this.searchInput.addEventListener(
        'input',
        debounce(function () {
          self.query = self.searchInput.value.trim();
          self.apply();
        }, SEARCH_DEBOUNCE_MS)
      );

      // Escape clears the field without losing focus.
      this.searchInput.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && self.searchInput.value) {
          e.preventDefault();
          self.searchInput.value = '';
          self.query = '';
          self.apply();
        }
      });
    }

    if (this.searchClear) {
      this.searchClear.addEventListener('click', function () {
        self.searchInput.value = '';
        self.query = '';
        self.searchInput.focus();
        self.apply();
      });
    }

    this.sortOptions.forEach(function (opt, i) {
      opt.addEventListener('click', function () {
        self.setSort(opt.getAttribute('data-cn-sort-option'));
        self.closeAllFacets();
        if (self.sortTrigger) self.sortTrigger.focus();
        self.apply();
      });

      // Listbox keyboard convention: arrows move between options, Home/End
      // jump to the ends. Escape is handled by the shared facet code.
      opt.addEventListener('keydown', function (e) {
        var next = null;
        if (e.key === 'ArrowDown') next = self.sortOptions[i + 1];
        else if (e.key === 'ArrowUp') next = self.sortOptions[i - 1];
        else if (e.key === 'Home') next = self.sortOptions[0];
        else if (e.key === 'End') next = self.sortOptions[self.sortOptions.length - 1];
        if (next) {
          e.preventDefault();
          next.focus();
        }
      });
    });

    // Opening the menu with the keyboard should land on the current choice.
    if (this.sortTrigger) {
      this.sortTrigger.addEventListener('keydown', function (e) {
        if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
        e.preventDefault();
        var facet = self.root.querySelector('[data-cn-facet="sort"]');
        self.closeAllFacets();
        self.openFacet(facet);
        var current = self.sortOptions.filter(function (o) {
          return o.getAttribute('aria-selected') === 'true';
        })[0];
        (current || self.sortOptions[0]).focus();
      });
    }

    this.clearBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        self.clearAll();
      });
    });
  };

  /* ------------------------------------------------------- facet dropdowns */

  /**
   * Each facet is a trigger + panel. Only one panel is open at a time; a
   * click outside, Escape, or opening another one closes it. The panel is
   * intentionally NOT closed on selection — people filter in batches.
   */
  ClientNews.prototype.initFacets = function () {
    var self = this;

    this.facets.forEach(function (facet) {
      var trigger = facet.querySelector('[data-cn-facet-trigger]');
      var panel = facet.querySelector('[data-cn-facet-panel]');
      if (!trigger || !panel) return;

      trigger.addEventListener('click', function (e) {
        e.stopPropagation();
        var isOpen = trigger.getAttribute('aria-expanded') === 'true';
        self.closeAllFacets();
        if (!isOpen) self.openFacet(facet);
      });

      // Escape closes and returns focus to the trigger.
      panel.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') {
          e.stopPropagation();
          self.closeAllFacets();
          trigger.focus();
        }
      });
    });

    // Click anywhere else closes the open panel.
    document.addEventListener('click', function (e) {
      if (!self.root.contains(e.target) || !e.target.closest('[data-cn-facet]')) {
        self.closeAllFacets();
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') self.closeAllFacets();
    });
  };

  ClientNews.prototype.openFacet = function (facet) {
    var trigger = facet.querySelector('[data-cn-facet-trigger]');
    var panel = facet.querySelector('[data-cn-facet-panel]');

    panel.hidden = false;
    trigger.setAttribute('aria-expanded', 'true');

    // Flip to right-aligned if the panel would run off the viewport.
    panel.classList.remove('is-right-aligned');
    var rect = panel.getBoundingClientRect();
    if (rect.right > window.innerWidth - 8) {
      panel.classList.add('is-right-aligned');
    }
  };

  ClientNews.prototype.closeAllFacets = function () {
    this.facets.forEach(function (facet) {
      var trigger = facet.querySelector('[data-cn-facet-trigger]');
      var panel = facet.querySelector('[data-cn-facet-panel]');
      if (!trigger || !panel) return;
      panel.hidden = true;
      trigger.setAttribute('aria-expanded', 'false');
    });
  };

  /* ------------------------------------------------------- stuck detection */

  /**
   * Adds .is-stuck once the toolbar reaches its sticky offset, which tightens
   * its padding and lifts it off the content. Uses rAF-throttled scroll rather
   * than an IntersectionObserver sentinel because the sticky offset is the
   * theme's dynamic --header-height.
   */
  ClientNews.prototype.initStuck = function () {
    if (!this.toolbar) return;
    var self = this;
    var ticking = false;

    function check() {
      ticking = false;
      var top = self.toolbar.getBoundingClientRect().top;
      var offset = parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue('--header-height')
      ) || 0;
      // 1px of tolerance absorbs sub-pixel rounding while scrolling.
      self.toolbar.classList.toggle('is-stuck', top <= offset + 1);
    }

    window.addEventListener(
      'scroll',
      function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(check);
      },
      { passive: true }
    );

    check();
  };

  /**
   * Selects a sort mode and syncs the listbox UI: aria-selected on the rows
   * and the trigger's visible label.
   * @param {string} value
   * @param {{silent?: boolean}} [opts] silent skips the re-render (used while
   *        hydrating from the URL, before the first apply()).
   */
  ClientNews.prototype.setSort = function (value, opts) {
    this.sort = value;

    this.sortOptions.forEach(function (opt) {
      var on = opt.getAttribute('data-cn-sort-option') === value;
      opt.setAttribute('aria-selected', on ? 'true' : 'false');
      if (on && this.sortValueEl) {
        var label = opt.querySelector('.cn-option__label');
        this.sortValueEl.textContent = label ? label.textContent.trim() : value;
      }
    }.bind(this));

    if (!opts || !opts.silent) this.writeStateToUrl();
  };

  ClientNews.prototype.clearAll = function () {
    GROUPS.forEach(
      function (g) {
        this.active[g].clear();
      }.bind(this)
    );
    this.query = '';
    if (this.searchInput) this.searchInput.value = '';
    this.apply();
    if (this.searchInput) this.searchInput.focus();
  };

  ClientNews.prototype.hasActiveFilters = function () {
    if (this.query) return true;
    return GROUPS.some(
      function (g) {
        return this.active[g].size > 0;
      }.bind(this)
    );
  };

  /* --------------------------------------------------------------- matching */

  /**
   * Does an item satisfy the current state?
   * @param {Element} item
   * @param {string}  [skipGroup] Ignore this facet — used when counting, so a
   *                              group's own selection doesn't zero its counts.
   */
  ClientNews.prototype.matches = function (item, skipGroup) {
    var d = item._cn;

    for (var i = 0; i < GROUPS.length; i++) {
      var g = GROUPS[i];
      if (g === skipGroup) continue;
      var set = this.active[g];
      if (set.size && !set.has(d[g])) return false;
    }

    if (this.query && d.search.indexOf(this.query.toLowerCase()) === -1) return false;

    return true;
  };

  /* ---------------------------------------------------------------- sorting */

  ClientNews.prototype.comparator = function () {
    switch (this.sort) {
      case 'oldest':
        return function (a, b) {
          return a._cn.date.localeCompare(b._cn.date);
        };
      case 'alphabetical':
        return function (a, b) {
          return a._cn.company.localeCompare(b._cn.company);
        };
      case 'amount':
        return function (a, b) {
          // Largest first; ties fall back to most recent.
          if (b._cn.amount !== a._cn.amount) return b._cn.amount - a._cn.amount;
          return b._cn.date.localeCompare(a._cn.date);
        };
      case 'newest':
      default:
        return function (a, b) {
          return b._cn.date.localeCompare(a._cn.date);
        };
    }
  };

  /* ------------------------------------------------------------ highlighting */

  /** Re-render headline/summary from cached text, wrapping query hits in <mark>. */
  ClientNews.prototype.highlight = function (item) {
    var d = item._cn;

    var render = function (el, text) {
      if (!el) return;
      if (!this.query) {
        el.textContent = text;
        return;
      }
      var re = new RegExp('(' + escapeRegExp(this.query) + ')', 'gi');
      el.innerHTML = escapeHtml(text).replace(re, '<mark class="cn-mark">$1</mark>');
    }.bind(this);

    render(d.headlineEl, d.headlineText);
    render(d.summaryEl, d.summaryText);
  };

  /* ------------------------------------------------------------------ apply */

  ClientNews.prototype.apply = function (opts) {
    opts = opts || {};
    var self = this;

    // 1. Which items survive the current filter state?
    var visible = this.items.filter(function (item) {
      return self.matches(item);
    });

    // 2. Order them, then hand the order to CSS rather than moving nodes.
    visible.sort(this.comparator());

    this.items.forEach(function (item) {
      item.classList.add('is-filtered-out');
      item.style.order = '';
      item.style.removeProperty('--cn-stagger');
    });

    visible.forEach(function (item, i) {
      item.classList.remove('is-filtered-out');
      item.style.order = String(i);
      if (!prefersReducedMotion) {
        item.style.setProperty('--cn-stagger', Math.min(i * STAGGER_STEP_MS, STAGGER_MAX_MS) + 'ms');
      }
      self.highlight(item);
    });

    // 3. Count, empty state, facet counts, URL.
    this.updateCount(visible.length);
    this.updateEmpty(visible.length === 0);
    this.updateFacetCounts();
    this.updatePillStates();
    this.updateFacetTriggers();

    if (!opts.initial) {
      this.writeStateToUrl();
      this.playRearrange();
    }
  };

  ClientNews.prototype.updateCount = function (n) {
    if (!this.countEl) return;
    this.countEl.textContent = 'Showing ' + n + ' ' + (n === 1 ? 'result' : 'results');
  };

  ClientNews.prototype.updateEmpty = function (isEmpty) {
    if (!this.emptyEl) return;
    this.emptyEl.classList.toggle('is-visible', isEmpty);
    if (this.grid) this.grid.hidden = isEmpty;
  };

  /**
   * Faceted counts: for each group, count matches ignoring that group's own
   * selection. A pill therefore shows how many results choosing it would give.
   */
  ClientNews.prototype.updateFacetCounts = function () {
    var self = this;

    GROUPS.forEach(function (group) {
      var pool = self.items.filter(function (item) {
        return self.matches(item, group);
      });

      self.pills.forEach(function (pill) {
        if (pill.getAttribute('data-cn-filter') !== group) return;
        var countEl = pill.querySelector('[data-cn-pill-count]');
        if (!countEl) return;

        var value = pill.getAttribute('data-value') || '';
        var n = value
          ? pool.filter(function (item) {
              return item._cn[group] === value;
            }).length
          : pool.length;

        countEl.textContent = n;
        // Nothing to find behind this pill — dim it, but leave it operable.
        pill.classList.toggle('is-empty', n === 0);
      });
    });
  };

  ClientNews.prototype.updatePillStates = function () {
    var self = this;
    this.pills.forEach(function (pill) {
      var group = pill.getAttribute('data-cn-filter');
      var value = pill.getAttribute('data-value') || '';
      var on = value ? self.active[group].has(value) : self.active[group].size === 0;
      pill.setAttribute('aria-pressed', on ? 'true' : 'false');
    });

    if (this.searchClear) {
      this.searchClear.classList.toggle('is-visible', !!this.query);
    }
  };

  /**
   * Keeps a collapsed facet honest about what it is hiding: the trigger shows
   * a count badge and an active border whenever that group holds a selection.
   */
  ClientNews.prototype.updateFacetTriggers = function () {
    var self = this;

    this.facets.forEach(function (facet) {
      var group = facet.getAttribute('data-cn-facet');
      var badge = facet.querySelector('[data-cn-facet-badge]');
      var n = self.active[group] ? self.active[group].size : 0;

      facet.classList.toggle('has-selection', n > 0);

      if (badge) {
        badge.textContent = n;
        badge.hidden = n === 0;
      }
    });

    // Toolbar Clear only exists while there is something to clear.
    if (this.clearToggle) {
      this.clearToggle.hidden = !this.hasActiveFilters();
    }
  };

  /** One-shot settle animation on the grid whenever the result set changes. */
  ClientNews.prototype.playRearrange = function () {
    if (prefersReducedMotion || !this.grid) return;
    var grid = this.grid;
    grid.classList.remove('is-rearranging');
    // Force a reflow so the animation restarts even on rapid keystrokes.
    void grid.offsetWidth;
    grid.classList.add('is-rearranging');
  };

  /* ----------------------------------------------------------- reveal on scroll */

  ClientNews.prototype.observeReveals = function () {
    var targets = Array.prototype.slice.call(this.root.querySelectorAll('[data-cn-reveal]'));
    if (!targets.length) return;

    // No IntersectionObserver (or reduced motion): show everything at once.
    if (prefersReducedMotion || !('IntersectionObserver' in window)) {
      targets.forEach(function (el) {
        el.classList.add('is-revealed');
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        // Stagger within each batch that crosses the threshold together.
        var i = 0;
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var el = entry.target;
          el.style.setProperty('--cn-stagger', Math.min(i * 60, 300) + 'ms');
          el.classList.add('is-revealed');
          observer.unobserve(el);
          i++;
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );

    targets.forEach(function (el) {
      observer.observe(el);
    });
  };

  /* ------------------------------------------------------------------- init */

  function init() {
    var roots = document.querySelectorAll('[data-cn-root]');
    Array.prototype.forEach.call(roots, function (root) {
      if (root._clientNews) return;
      root._clientNews = new ClientNews(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-initialise when the section is re-rendered inside the theme editor.
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelector('[data-cn-root]')) init();
  });
})();
